import React from 'react';
import { Link as GatsbyLink } from 'gatsby';
import { FormattedMessage } from 'react-intl';
import {
  Grid, Link, Heading,
  RichText,
} from '@printdeal/ui-library';
import { Markdown } from '@printdeal/markdown/react';
import Breadcrumb from '../Breadcrumb';
import HelpCenterCategories from './HelpCenterCategories';
import {
  HelpCenterArticleContent,
  LinkedSupportArticle,
  LinkedFaq,
  TaggedSupportArticles,
  TaggedFaq,
} from '../../types/helpCenter/helpCenter';
import HelpCenterProductGallery from './HelpCenterProductGallery';
import HelpCenterLinkedFaq from './HelpCenterLinkedFaq';
import HelpCenterLinkedSupportArticles from './HelpCenterLinkedSupportArticles';
import HelpCenterHeroImage from './HelpCenterHeroImage';
import PageRenderer from '../PageRenderer';
import { useLocation } from '../../hooks/useSafeLocation';

interface SupportArticleProps {
  article: HelpCenterArticleContent;
  dashboardUrl: string;
  relatedPages: TaggedSupportArticles;
  relatedContent?: TaggedFaq | null;
}
const SupportArticle = ({
  article,
  dashboardUrl,
  relatedPages,
  relatedContent = null,
}: SupportArticleProps) => {
  const { origin } = useLocation();
  const {
    title, heroImage, gallery, category,
  } = article;

  //  - using two methods, because otherwise TypeScript gets nasty
  const getLinkedFaq = (linked: LinkedFaq[], related: TaggedFaq | null) => {
    if (linked && linked.length > 0) {
      return linked;
    }
    if (related?.edges && related.edges?.length > 0) {
      return related.edges.map(({ node }) => ({
        ...node,
      }));
    }
    return [];
  };
  const getLinkedSupportArticles = (
    linked: LinkedSupportArticle[],
    related: TaggedSupportArticles,
  ) => {
    if (linked && linked.length > 0) {
      return linked;
    }
    if (related?.edges && related.edges?.length > 0) {
      return related.edges.map(({ node }) => ({
        ...node,
      }));
    }
    return [];
  };
  const linkedFaq = getLinkedFaq(article.linkedFaq, relatedContent);
  const linkedSupportArticles = getLinkedSupportArticles(
    article.linkedSupportArticles,
    relatedPages,
  );

  return (
    <Grid className="pd-grid-cols-12">
      <Grid.Item className="pd-col-span-12 pd-hidden md:pd-col-span-3 md:pd-block">
        <HelpCenterCategories supportArticleCategory={category} />
      </Grid.Item>
      <Grid.Item className="pd-col-span-12 md:pd-col-span-1" />
      <Grid.Item className="pd-col-span-12 pd-flex pd-flex-col pd-gap-12 md:pd-col-span-8">
        <Breadcrumb>
          <Breadcrumb.Item to={dashboardUrl}>
            <FormattedMessage
              id="helpcenter.breadcrumb.home"
              defaultMessage="Help center"
            />
          </Breadcrumb.Item>
          {category && (
            <Breadcrumb.Item to={category.path}>
              {category.title}
            </Breadcrumb.Item>
          )}
          <Breadcrumb.Item isCurrentPage>{title}</Breadcrumb.Item>
        </Breadcrumb>
        <header className="pd-flex pd-flex-col pd-gap-8 md:pd-flex-row">
          <div className="pd-grow">
            <Heading as="h1">{title}</Heading>
            {article.summary && (
              <RichText className="pd-mt-4">
                <Markdown origin={origin}>{article.summary.summary}</Markdown>
              </RichText>
            )}
          </div>
          {gallery && gallery.images && (
            <figure className="pd-min-w-60 pd-shrink-0">
              <HelpCenterProductGallery gallery={gallery} title={title} />
            </figure>
          )}
          {!gallery?.images && heroImage && (
            <figure className="pd-min-w-60 pd-shrink-0">
              <HelpCenterHeroImage heroImage={heroImage} />
            </figure>
          )}
        </header>

        {article.pageBuilder ? (
          <PageRenderer data={article.pageBuilder} />
        ) : (
          <>
            {article.content && (
              <RichText>
                <Markdown origin={origin}>{article.content?.content}</Markdown>
              </RichText>
            )}
          </>
        )}

        <div className="[&>div]:pd-rounded [&>div]:pd-border" id="articleRating" />
        {linkedFaq && linkedFaq.length > 0 && (
          <>
            <hr />
            <Grid className="pd-grid-cols-12">
              <Grid.Item className="pd-col-span-12 md:pd-col-span-9">
                <HelpCenterLinkedFaq linkedFaq={linkedFaq} />
              </Grid.Item>
            </Grid>
          </>
        )}
        {linkedSupportArticles && linkedSupportArticles.length > 0 && (
          <HelpCenterLinkedSupportArticles linkedSupportArticles={linkedSupportArticles} />
        )}
        {category && (
          <Link
            key={category?.title}
            as={GatsbyLink}
            variant="secondary"
            to={category.path}
            className="pd-text-sm md:pd-hidden"
            iconBefore="chevron-left"
          >
            <FormattedMessage
              id="helpcenter.category.backToPage"
              defaultMessage="Back to {page}"
              values={{
                page: category.title,
              }}
            />
          </Link>
        )}
      </Grid.Item>
    </Grid>
  );
};
export default SupportArticle;
